import {
  AgentOperatorBackrefSchema,
  AgentProfileSchema,
  EarningsLedgerEntrySchema,
  PlatformBrandSchema,
} from '@kilat/shared';

import { createRepo } from './_base.js';

/**
 * Platform brands — public-read via firestore.rules. We read the
 * brand bound to the signed-in agent (via `agent.platformBrandId`)
 * to compose the brand-correct marketing URL for the agent's
 * referral code.
 */
export const platformBrandsRepo = createRepo({
  path: 'platformBrands',
  schema: PlatformBrandSchema,
  name: 'platformBrands',
});

/**
 * Per-agent client repos. Tenant isolation is enforced upstream by
 * security rules: an agent can only read documents under their own
 * `agents/{agentId}/...` path.
 */

/** Agent profile doc at `agents/{agentId}`. */
export const agentsRepo = createRepo({
  path: 'agents',
  schema: AgentProfileSchema,
  name: 'agents',
});

/**
 * `agents/{agentId}/earningsLedger/{entryId}` — append-only ledger of
 * commission accruals, clawbacks, and adjustments. Written by:
 *   - `commissionAccrue` Cloud Function (kind: 'accrued', Sprint 37)
 *   - `commissionClawback` Cloud Function (kind: 'clawed_back', Sprint 38+)
 *   - super-admin adjustments (kind: 'adjustment')
 *   - payout run (kind: 'paid' — emitted as the payout settles)
 */
export function earningsLedgerRepoFor(agentId: string) {
  return createRepo({
    path: `agents/${agentId}/earningsLedger`,
    schema: EarningsLedgerEntrySchema,
    name: `agents/${agentId}/earningsLedger`,
  });
}

/**
 * `agents/{agentId}/operators/{tenantId}` — back-ref written by
 * `tenantBootstrap` and patched by `commissionAccrue`. Drives the
 * agent PWA's /operators page; agents cannot read `tenants/{tid}`
 * directly so this denormalised back-ref is the only source.
 */
export function operatorsBackrefRepoFor(agentId: string) {
  return createRepo({
    path: `agents/${agentId}/operators`,
    schema: AgentOperatorBackrefSchema,
    name: `agents/${agentId}/operators`,
  });
}
